import './HomePage.css';
import React, { useEffect, useState, useMemo } from 'react';
import { FetchRandomSightseeing, FetchRandomSightseeingBulkCategory, SightseeingPost } from './Sightseeing/SightseeingRequests';
import { FetchRandomFood, FetchRandomFoodBulkCategory, FoodPost } from './Food/FoodRequests';
import { personas } from './Sightseeing/SightseeingLayout';
import { foodTypes } from './Food/FoodLayout';
import { NewPostForm } from './TicketForm';
import { IsHtml } from './Utils/Utility';
import { useNavigate } from 'react-router-dom';
import { GlobeScene } from './SpinningGlobe';
import { MaterialIcon, ParseCoordinates } from './Utils/CountryUtility';
import { Helmet } from 'react-helmet';
import { navigateToFoodWithCursor, navigateToSightseeingWithCursor } from './Utils/TopicCursorNavigation';
import { ComposableMap, Geographies, Geography, Marker, Line } from 'react-simple-maps';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

type PinboardPreviewPost = SightseeingPost & { pinCoordinates: [number, number] };
const MemoizedGlobeScene = React.memo(GlobeScene);

function HomePage() {
  const navigate = useNavigate();
  const [sightseeingData, setSightseeingData] = useState<SightseeingPost[]>([]); // State to store search results from the database
  const [sightseeingCategories, setSightseeingCategories] = useState<Record<string, SightseeingPost[]>>({}); // State to store search results from the database
  const [foodData, setFoodData] = useState<FoodPost[]>([]); // State to store search results from the database
  const [foodCategories, setFoodCategories] = useState<Record<string, FoodPost[]>>({}); // State to store search results from the database
  const [openIndexSightseeing, setOpenIndexSightseeing] = useState(0);
  const [openIndexFood, setOpenIndexFood] = useState(0);
  const [pinboardPreviewData, setPinboardPreviewData] = useState<SightseeingPost[]>([]);
  const [selectedPersona, setSelectedPersona] = useState<(typeof personas)[number]>(personas[0]);
  const [selectedFoodType, setSelectedFoodType] = useState<(typeof foodTypes)[number]>(foodTypes[0]);
  const selectedTravelCategory = sightseeingCategories[selectedPersona.id]?.[0];
  const selectedFoodCategory = foodCategories[selectedFoodType.id]?.[0];
  const geoJson = '/assets/maps/world-50m.json';

  useEffect(() => {
    async function fetchsightseeingData() {
      try {
        const data = await FetchRandomSightseeing(5, 'HomePage', undefined, undefined);
        setSightseeingData(data);
        setOpenIndexSightseeing(0); // reset to first when options change
      } catch (error) {
        console.error('Error fetching sightseeing data:', error);
      }
    }
    fetchsightseeingData();

    const intervalId = setInterval(() => {
      fetchsightseeingData(); // Fetch every 10 minutes when user is on the page for to long.
    }, 600000); // 600,000 ms = 10 minutes

    return () => clearInterval(intervalId); // Cleanup when unmounting
  }, []);

  useEffect(() => {
    async function fetchFoodData() {
      try {
        const data = await FetchRandomFood(5, 'HomePage', undefined, undefined);
        setFoodData(data);
        setOpenIndexFood(0); // reset to first when options change
      } catch (error) {
        console.error('Error fetching food data:', error);
      }
    }
    fetchFoodData();

    const intervalId = setInterval(() => {
      fetchFoodData();
    }, 600000);

    return () => clearInterval(intervalId); // Cleanup when unmounting
  }, []);

  useEffect(() => {
    async function fetchAllSightseeingCategories() {
      try {
        const data = await FetchRandomSightseeingBulkCategory(1, 'HomePage', undefined);
        // backend already returns { categoryName: [items] }
        setSightseeingCategories(data);
      } catch (error) {
        console.error('Error fetching bulk sightseeing categories:', error);
      }
    }

    fetchAllSightseeingCategories();
  }, []);

  useEffect(() => {
    async function fetchAllFoodCategories() {
      try {
        const data = await FetchRandomFoodBulkCategory(1, 'HomePage', undefined);
        setFoodCategories(data);
      } catch (error) {
        console.error('Error fetching bulk food categories:', error);
      }
    }

    fetchAllFoodCategories();
  }, []);

  useEffect(() => {
    async function fetchPinboardPreviewData() {
      try {
        const data = await FetchRandomSightseeing(3, 'HomePagePinboardWorld', undefined, undefined);
        setPinboardPreviewData(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching pinboard preview data:', error);
      }
    }

    fetchPinboardPreviewData();

    const intervalId = setInterval(() => {
      fetchPinboardPreviewData();
    }, 600000);

    return () => clearInterval(intervalId);
  }, []);

  const globe = useMemo(() => <MemoizedGlobeScene />, []);
  const pinboardPreviewPosts = useMemo<PinboardPreviewPost[]>(
    () =>
      pinboardPreviewData
        .map((post) => {
          const [lat, lon] = ParseCoordinates(post.coordinates || '');
          return {
            ...post,
            pinCoordinates: [lon, lat] as [number, number],
          };
        })
        .filter((post) => !Number.isNaN(post.pinCoordinates[0]) && !Number.isNaN(post.pinCoordinates[1])),
    [pinboardPreviewData],
  );

  return (
    <div className="HomePage">
      <Helmet>
        <title>Travel Planning Guides, Food & Sightseeing | Living Out Of A Backpack</title>
        <link rel="canonical" href={`https://livingoutofabackpack.com/home/`} />
        <meta name="description" content="Plan better trips with country travel guides, local food discoveries, sightseeing ideas, and practical travel statistics from around the world." />
        <meta
          name="keywords"
          content="
        travel destinations, 
        food around the world, 
        best restaurants, 
        sightseeing tips, 
        ancient historical sites, 
        world cuisine, 
        cultural travel, 
        travel statistics, 
        currency exchange, 
        local weather data, 
        backpacking tips, 
        solo travel ideas, 
        budget travel, 
        travel maps, 
        global travel topics, 
        country guides, 
        tourism insights, 
        lifestyle travel, 
        UNESCO sites, 
        regional dishes"
        />
        <meta property="og:title" content="Travel Planning Guides, Food & Sightseeing | Living Out Of A Backpack" />
        <meta property="og:description" content="Discover country-by-country travel ideas, must-visit spots, local cuisine, and useful travel stats to plan your next trip." />
        <meta property="og:url" content="https://livingoutofabackpack.com/home/" />
      </Helmet>

      <div className="Title">Discover New Places Around The World</div>

      <section className="Paragraph1">
        <div className="collapsible-row">
          {sightseeingData.map((item, index) => (
            <div
              key={item.id}
              className={`collapsible-item ${openIndexSightseeing === index ? 'open' : 'closed'}`}
              onClick={async () => {
                if (openIndexSightseeing === index) {
                  await navigateToSightseeingWithCursor({ item, navigate });
                } else {
                  setOpenIndexSightseeing(index);
                }
              }}
            >
              {openIndexSightseeing === index && (
                <div className="LocationLabel">
                  <div className="LocationText">{item.place || item.activity}</div>
                  <MaterialIcon iconName="location_on" style={{ color: '#4CAF50' }} />
                </div>
              )}

              <div className="image-container">
                <img
                  src={`${backendUrl}/django-dynamic/pictures/${item.images[0]}`}
                  alt={item.place || item.activity || 'Travel Spot'}
                  title={item.place || item.activity || 'Travel Spot'}
                  className="preview-image"
                />

                {item.attributions?.[0] && (
                  <div className="attribution">
                    <div className="attribution-content">{IsHtml(item.attributions[0]) ? <div dangerouslySetInnerHTML={{ __html: item.attributions[0] }} /> : <span>{item.attributions[0]}</span>}</div>
                  </div>
                )}

                <div className="iso-badge">
                  {item.iso}
                  <img
                    src={`https://flagcdn.com/w320/${item.iso.toLowerCase()}.png`}
                    alt={`${item.iso} flag`}
                    style={{
                      width: '100%',
                      maxWidth: '20px',
                      height: 'auto',
                      borderRadius: '4px',
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="WebsiteDescription">
          <div className="WebsiteDescriptionList">
            <li>Find New Places to add to your bucket-list.</li>
            <li>Plan-out your travel agenda with our handy tools.</li>
          </div>
          <div className="MapCornersWrap">
            <div className="WorldCornerCard" onClick={() => navigate('/worldmap')}>
              <div className="GlobeAnimation">{globe}</div>
              <div className="CornerCardLabel">World Map</div>
            </div>

            <div className="PinboardCornerCard" onClick={() => navigate('/travelpinboard')}>
              <div className="pinboard-preview-map" role="img" aria-label="Travel pinboard preview map">
                <ComposableMap projectionConfig={{ scale: 145 }}>
                  <Geographies geography={geoJson}>
                    {({ geographies }) =>
                      geographies.map((geo) => (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          style={{
                            default: {
                              fill: 'var(--surface-alt)',
                              outline: 'none',
                              stroke: 'var(--border-strong)',
                              strokeWidth: 0.2,
                            },
                            hover: {
                              fill: 'var(--surface-alt)',
                              outline: 'none',
                              stroke: 'var(--border-strong)',
                              strokeWidth: 0.2,
                            },
                            pressed: {
                              fill: 'var(--surface-alt)',
                              outline: 'none',
                              stroke: 'var(--border-strong)',
                              strokeWidth: 0.2,
                            },
                          }}
                        />
                      ))
                    }
                  </Geographies>

                  {pinboardPreviewPosts.slice(1).map((post, index) => {
                    const previousPost = pinboardPreviewPosts[index];
                    return (
                      <Line
                        key={`home-pinboard-line-${post.id}`}
                        from={previousPost.pinCoordinates}
                        to={post.pinCoordinates}
                        stroke="var(--text-dim)"
                        strokeWidth={1.2}
                        strokeLinecap="round"
                        strokeDasharray="3 2"
                      />
                    );
                  })}

                  {pinboardPreviewPosts.map((post, index) => (
                    <Marker key={`home-pinboard-marker-${post.id}`} coordinates={post.pinCoordinates}>
                      <g>
                        <circle r={5.6} fill="var(--error-text)" stroke="var(--surface-elevated)" strokeWidth={1.3} />
                        <circle cy={1.2} r={1.5} fill="var(--surface-elevated)" />
                        <line x1="0" y1="5.6" x2="0" y2="10.6" stroke="var(--border-strong)" strokeWidth={1.3} strokeLinecap="round" />
                        <text x="0" y="-7" textAnchor="middle" fontSize="7" fill="var(--text-color)">
                          {index + 1}
                        </text>
                      </g>
                    </Marker>
                  ))}
                </ComposableMap>
              </div>
              <div className="CornerCardLabel">Pinboard</div>
            </div>
          </div>
        </div>

        <div className="persona-tab">
          <div className="sidebar">
            {personas.map((persona) => (
              <div key={persona.id} className={`mini-tile ${selectedPersona?.id === persona.id ? 'selected' : ''}`} onClick={() => setSelectedPersona(persona)}>
                <span className="emoji">{persona.emoji}</span>
              </div>
            ))}
          </div>

          <div className="main-tile">
            {selectedPersona && selectedTravelCategory ? (
              <div
                className="main-tile-content"
                key={selectedTravelCategory.id}
                onClick={() => {
                  navigate(`/personas/${encodeURIComponent(selectedPersona.id)}`);
                }}
              >
                <div className="LocationLabel">
                  <div className="LocationText">{selectedTravelCategory.region}</div>
                  <MaterialIcon iconName="location_on" style={{ color: '#4CAF50' }} />
                </div>
                <div className="image-container">
                  <img
                    src={`${backendUrl}/django-dynamic/pictures/${selectedTravelCategory.images?.[0]}`}
                    alt={selectedTravelCategory.place || selectedTravelCategory.activity || 'Travel Spot'}
                    title={selectedTravelCategory.place || selectedTravelCategory.activity || 'Travel Spot'}
                    className="persona-image"
                    onClick={(event) => {
                      event.stopPropagation();
                      navigate(`/personas/${encodeURIComponent(selectedPersona.id)}`);
                    }}
                  />

                  {selectedTravelCategory.attributions?.[0] && (
                    <div className="attribution">
                      <div className="attribution-content">
                        {IsHtml(selectedTravelCategory.attributions[0]) ? (
                          <div dangerouslySetInnerHTML={{ __html: selectedTravelCategory.attributions[0] }} />
                        ) : (
                          <span>{selectedTravelCategory.attributions[0]}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="label">
                  {selectedPersona.label}
                  {selectedPersona.emoji}
                </div>
                <span className="text">{selectedPersona.explanation}</span>
              </div>
            ) : (
              <div className="label">Select a persona</div>
            )}
          </div>
        </div>

      
        <div className="collapsible-row">
          {foodData.map((item, index) => (
            <div
              key={item.id}
              className={`collapsible-item ${openIndexFood === index ? 'open' : 'closed'}`}
              onClick={async () => {
                if (openIndexFood === index) {
                  await navigateToFoodWithCursor({ item, navigate });
                } else {
                  setOpenIndexFood(index);
                }
              }}
            >
              {openIndexFood === index && (
                <div className="LocationLabel">
                  <div className="LocationText">{item.dish || item.restaurant}</div>
                  <MaterialIcon iconName="restaurant_menu" style={{ color: '#4CAF50' }} />
                </div>
              )}

              <div className="image-container">
                <img
                  src={`${backendUrl}/django-dynamic/pictures/${item.images[0]}`}
                  alt={item.dish || item.restaurant || 'Food post'}
                  title={item.dish || item.restaurant || 'Food Post'}
                  className="preview-image"
                />

                {item.attributions?.[0] && (
                  <div className="attribution">
                    <div className="attribution-content">{IsHtml(item.attributions[0]) ? <div dangerouslySetInnerHTML={{ __html: item.attributions[0] }} /> : <span>{item.attributions[0]}</span>}</div>
                  </div>
                )}

                <div className="iso-badge">
                  {item.iso}
                  <img
                    src={`https://flagcdn.com/w320/${item.iso.toLowerCase()}.png`}
                    alt={`${item.iso} flag`}
                    style={{
                      width: '100%',
                      maxWidth: '20px',
                      height: 'auto',
                      borderRadius: '4px',
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="food-type-tab">
          <div className="sidebar">
            {foodTypes.map((foodType) => (
              <div key={foodType.id} className={`mini-tile ${selectedFoodType?.id === foodType.id ? 'selected' : ''}`} onClick={() => setSelectedFoodType(foodType)}>
                <span className="emoji">{foodType.emoji}</span>
              </div>
            ))}
          </div>

          <div className="main-tile">
            {selectedFoodType && selectedFoodCategory ? (
              <div
                className="main-tile-content"
                key={selectedFoodCategory.id}
                onClick={() => {
                  navigate(`/foodtypes/${encodeURIComponent(selectedFoodType.id)}`);
                }}
              >
                <div className="LocationLabel">
                  <div className="LocationText">{selectedFoodCategory.dish}</div>
                  <MaterialIcon iconName="restaurant_menu" style={{ color: '#4CAF50' }} />
                </div>

                <div className="image-container">
                  <img
                    src={`${backendUrl}/django-dynamic/pictures/${selectedFoodCategory.images?.[0]}`}
                    alt={selectedFoodCategory.dish || selectedFoodCategory.restaurant || 'Food Post'}
                    title={selectedFoodCategory.dish || selectedFoodCategory.restaurant || 'Food Post'}
                    className="food-type-image"
                    onClick={(event) => {
                      event.stopPropagation();
                      navigate(`/foodtypes/${encodeURIComponent(selectedFoodType.id)}`);
                    }}
                  />

                  {selectedFoodCategory.attributions?.[0] && (
                    <div className="attribution">
                      <div className="attribution-content">
                        {IsHtml(selectedFoodCategory.attributions[0]) ? (
                          <div dangerouslySetInnerHTML={{ __html: selectedFoodCategory.attributions[0] }} />
                        ) : (
                          <span>{selectedFoodCategory.attributions[0]}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="label">
                  {selectedFoodType.label}
                  {selectedFoodType.emoji}
                </div>
                <span className="text">{selectedFoodType.explanation}</span>
              </div>
            ) : (
              <div className="label">Select a food type</div>
            )}
          </div>
        </div>

        <div className="home-ticket-form">
          <NewPostForm />
        </div>
      </section>

      <div className="AboutSection">
        <div className="AboutTitle">About</div>
        <div className="AboutText">
          Have you ever visited a country and felt unsure of what to do? Our website is here to assist you with all your travel needs. We help you discover exciting
          places, top attractions, and the best restaurants to visit.
        </div>
      </div>

      <div className="CatchPhrase">The world's your map🌍 — we're your compass🧭</div>

      <div className="MobileButtonsHomePage">
        <div className="PolicyButton">
          <div className={`PolicyButtonLink`} onClick={() => navigate('/disclaimerandpolicypage')}>
            Cookie Preferences & Policy
          </div>
        </div>

        <div className="ContactUsButton">
          <div className={`ContactUsButtonLink`} onClick={() => navigate('/contactuspage')}>
            Contact Us & Copyright
          </div>
        </div>
      </div>
    </div>
  );
}

export default HomePage;
